package fr.manyverse.app.presentation.screens

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExtendedFloatingActionButton
import androidx.compose.material3.FilledIconButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.VerticalDivider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.lerp
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.manyverse.app.domain.model.Universe
import fr.manyverse.app.presentation.components.ErrorState
import fr.manyverse.app.presentation.components.SkeletonList
import fr.manyverse.app.presentation.components.UniverseEditForm
import fr.manyverse.app.presentation.layout.WindowSizeContainer
import fr.manyverse.app.presentation.layout.WindowWidth
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.theme.rememberUniverseColorScheme
import fr.manyverse.app.presentation.viewmodel.UniversesViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.back
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.create_universe
import manyverse.shared.generated.resources.delete_universe
import manyverse.shared.generated.resources.delete_universe_confirm
import manyverse.shared.generated.resources.edit_universe
import manyverse.shared.generated.resources.error_loading_universes
import manyverse.shared.generated.resources.my_universes
import manyverse.shared.generated.resources.select_universe_prompt
import manyverse.shared.generated.resources.universe_no_custom_yet
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun UniversesListScreen(
    userId: String,
    onNavigateBack: () -> Unit,
    onCreateUniverse: () -> Unit,
    onEditUniverse: (String) -> Unit,
    viewModel: UniversesViewModel = koinViewModel()
) {
    val listState by viewModel.listState.collectAsStateWithLifecycle()
    val deleteState by viewModel.deleteState.collectAsStateWithLifecycle()
    var pendingDelete by remember { mutableStateOf<Universe?>(null) }
    var selectedUniverseId by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(userId) {
        viewModel.loadUniverses(userId)
    }

    LaunchedEffect(deleteState) {
        if (deleteState is AsyncUiState.Success) {
            pendingDelete = null
            viewModel.resetDeleteState()
        }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(stringResource(Res.string.my_universes)) },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = stringResource(Res.string.back)
                        )
                    }
                }
            )
        },
        floatingActionButton = {
            ExtendedFloatingActionButton(
                onClick = onCreateUniverse,
                icon = { Icon(Icons.Default.Add, contentDescription = null) },
                text = { Text(stringResource(Res.string.create_universe)) }
            )
        }
    ) { padding ->
        Box(Modifier.fillMaxSize().padding(padding)) {
            when (val state = listState) {
                is AsyncUiState.Loading -> {
                    SkeletonList(modifier = Modifier.fillMaxSize())
                }

                is AsyncUiState.Error -> {
                    ErrorState(
                        message = state.message,
                        onRetry = { viewModel.loadUniverses(userId) },
                        title = stringResource(Res.string.error_loading_universes)
                    )
                }

                is AsyncUiState.Success -> {
                    if (state.data.isEmpty()) {
                        Text(
                            text = stringResource(Res.string.universe_no_custom_yet),
                            style = MaterialTheme.typography.bodyLarge,
                            modifier = Modifier
                                .align(Alignment.Center)
                                .padding(24.dp)
                        )
                    } else {
                        WindowSizeContainer { windowWidth ->
                            if (windowWidth == WindowWidth.EXPANDED) {
                                // List-detail: universe list on the left, inline edit pane on the right.
                                Row(modifier = Modifier.fillMaxSize()) {
                                    UniverseList(
                                        universes = state.data,
                                        onEdit = { selectedUniverseId = it.id },
                                        onDelete = { pendingDelete = it },
                                        modifier = Modifier.weight(1f)
                                    )
                                    VerticalDivider()
                                    val selected = state.data.find { it.id == selectedUniverseId }
                                    Box(
                                        modifier = Modifier
                                            .weight(1f)
                                            .fillMaxHeight(),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        if (selected != null) {
                                            UniverseEditForm(
                                                userId = userId,
                                                editingId = selected.id,
                                                initialUniverse = selected,
                                                viewModel = viewModel,
                                                onCancel = { selectedUniverseId = null }
                                            )
                                        } else {
                                            Text(
                                                text = stringResource(Res.string.select_universe_prompt),
                                                style = MaterialTheme.typography.bodyLarge,
                                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                                modifier = Modifier.padding(24.dp)
                                            )
                                        }
                                    }
                                }
                            } else {
                                UniverseList(
                                    universes = state.data,
                                    onEdit = { onEditUniverse(it.id) },
                                    onDelete = { pendingDelete = it },
                                    modifier = Modifier.fillMaxSize()
                                )
                            }
                        }
                    }
                }

                else -> Unit
            }
        }
    }

    pendingDelete?.let { target ->
        val dismissDelete = {
            viewModel.resetDeleteState()
            pendingDelete = null
        }
        AlertDialog(
            onDismissRequest = dismissDelete,
            title = { Text(stringResource(Res.string.delete_universe)) },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(stringResource(Res.string.delete_universe_confirm))
                    (deleteState as? AsyncUiState.Error)?.let {
                        Text(
                            text = it.message,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodySmall
                        )
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = { viewModel.delete(target.id, userId) },
                    enabled = deleteState !is AsyncUiState.Loading
                ) {
                    Text(stringResource(Res.string.delete_universe))
                }
            },
            dismissButton = {
                TextButton(onClick = dismissDelete) {
                    Text(stringResource(Res.string.cancel))
                }
            }
        )
    }
}

@Composable
private fun UniverseList(
    universes: List<Universe>,
    onEdit: (Universe) -> Unit,
    onDelete: (Universe) -> Unit,
    modifier: Modifier = Modifier
) {
    LazyColumn(
        modifier = modifier,
        contentPadding = PaddingValues(16.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        items(universes, key = { it.id }) { universe ->
            UniverseRow(
                universe = universe,
                onEdit = { onEdit(universe) },
                onDelete = { onDelete(universe) }
            )
        }
    }
}

@Composable
private fun UniverseRow(
    universe: Universe,
    onEdit: () -> Unit,
    onDelete: () -> Unit
) {
    val scheme = rememberUniverseColorScheme(universe.config, isSystemInDarkTheme())
    // Couleur vive de l'univers utilisée comme accent (barre gauche + nom)
    val accentColor = scheme.primary
    // Fond très légèrement teinté de l'accent, sur la base neutre du surface de l'app
    val cardBackground = lerp(MaterialTheme.colorScheme.surface, accentColor, 0.10f)
    val borderColor = accentColor.copy(alpha = 0.45f)

    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = cardBackground),
        border = BorderStroke(1.dp, borderColor),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
    ) {
        Row(modifier = Modifier.height(IntrinsicSize.Min)) {
            // Barre d'accent verticale à gauche
            Box(
                modifier = Modifier
                    .width(4.dp)
                    .fillMaxHeight()
                    .background(accentColor)
            )

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp, vertical = 8.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = universe.displayName,
                    style = MaterialTheme.typography.titleMedium,
                    color = accentColor,
                    modifier = Modifier.weight(1f)
                )
                FilledIconButton(
                    onClick = onEdit,
                    colors = IconButtonDefaults.filledIconButtonColors(
                        containerColor = MaterialTheme.colorScheme.surfaceVariant,
                        contentColor = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                ) {
                    Icon(
                        Icons.Default.Edit,
                        contentDescription = stringResource(Res.string.edit_universe)
                    )
                }
                FilledIconButton(
                    onClick = onDelete,
                    colors = IconButtonDefaults.filledIconButtonColors(
                        containerColor = MaterialTheme.colorScheme.errorContainer,
                        contentColor = MaterialTheme.colorScheme.onErrorContainer
                    )
                ) {
                    Icon(
                        Icons.Default.Delete,
                        contentDescription = stringResource(Res.string.delete_universe)
                    )
                }
            }
        }
    }
}
